import { withDefault } from "collection-utils";
import type { SchemaInspector } from ".";
import type { TableName } from "../common/database-strings";
import { isTableName } from "../common/database-strings";
import type {
    ArrayItemType,
    ClassOrArrayScreenDescription,
    EditScreenDescription,
    ScreenDescription,
    TableRefGlideType,
} from "../common/description";
import { getTableName, getTableRefTableName, isSingleRelationType, ScreenDescriptionKind } from "../common/description";

export const chatScreenName = "chat";
export const shoppingCartScreenName = "shopping-cart";
export const emailAuthScreenName = "email-auth";
export const buyScreenName = "buy-screen";
export const userProfileScreenName = "user-profile";
export const webViewScreenName = "web-view";
export const shareScreenName = "share";
export const sendAppFeedbackScreenName = "author-feedback";

const classScreenPrefix = "class-";
const arrayScreenPrefix = "array-";
const arrayTableScreenPrefix = arrayScreenPrefix + "table-";
export const formScreenPrefix = "form-";
export const freeScreenPrefix = "free-";
const menuScreenPrefix = "menu-";
const variantPrefix = "variant-";
const addClassScreenPrefix = "add-class-";
const editClassScreenPrefix = "edit-class-";

export function classScreenName(nameOrRef: TableName | TableRefGlideType): string {
    return classScreenPrefix + getTableRefTableName(nameOrRef).name;
}

export function arrayScreenName(schema: SchemaInspector, itemType: TableRefGlideType): string;
export function arrayScreenName(schema: SchemaInspector, itemType: ArrayItemType): string | undefined;
export function arrayScreenName(schema: SchemaInspector, itemType: ArrayItemType): string | undefined {
    if (isSingleRelationType(itemType)) {
        let name = getTableRefTableName(itemType);
        if (!isTableName(name)) {
            const t = schema.findTable(name);
            if (t === undefined) return undefined;
            name = getTableName(t);
        }
        return arrayTableScreenPrefix + name.name;
    } else {
        return arrayScreenPrefix + "primitive-" + itemType.kind;
    }
}

export function isDefaultArrayScreenName(screenName: string): boolean {
    return screenName.startsWith(arrayScreenPrefix);
}

export function getClassScreenTableName(screenName: string): string | undefined {
    if (screenName.startsWith(classScreenPrefix)) {
        return screenName.substring(classScreenPrefix.length);
    }
    return undefined;
}

export function getArrayScreenTableName(screenName: string): string | undefined {
    if (screenName.startsWith(arrayTableScreenPrefix)) {
        return screenName.substring(arrayTableScreenPrefix.length);
    }
    return undefined;
}

export function getClassOrArrayScreenTableName(screenName: string): string | undefined {
    return withDefault(getClassScreenTableName(screenName), getArrayScreenTableName(screenName));
}

export function variantScreenNamePrefix(type: TableRefGlideType): string {
    return `${variantPrefix}${classScreenName(type)}-`;
}

export function addClassScreenName(nameOrRef: TableName | TableRefGlideType): string {
    const name = getTableRefTableName(nameOrRef);
    return addClassScreenPrefix + name.name;
}

export function editClassScreenName(nameOrRef: TableName | TableRefGlideType): string {
    const name = getTableRefTableName(nameOrRef);
    return editClassScreenPrefix + name.name;
}

export function isAddClassScreenName(screenName: string): boolean {
    return screenName.startsWith(addClassScreenPrefix);
}

export function isEditClassScreenName(screenName: string): boolean {
    return screenName.startsWith(editClassScreenPrefix);
}

export function isVariantScreenName(screenName: string): boolean {
    return screenName.startsWith(variantPrefix);
}

export function isAddOrEditOrFormScreenDescription(
    screenName: string,
    desc: ScreenDescription
): desc is EditScreenDescription {
    if (desc.kind !== ScreenDescriptionKind.Class) return false;
    return isEditClassScreenName(screenName) || isAddClassScreenName(screenName) || desc.isForm === true;
}

export function isFormScreenName(name: string): boolean {
    return name.startsWith(formScreenPrefix);
}

export function isFreeScreenName(name: string): boolean {
    return name.startsWith(freeScreenPrefix);
}

export function isFreeScreen(name: string, desc: ScreenDescription): desc is ClassOrArrayScreenDescription {
    if (desc.kind !== ScreenDescriptionKind.Class && desc.kind !== ScreenDescriptionKind.Array) return false;
    return isFreeScreenName(name);
}

export function getMenuScreenName(menuID: string, primaryKeyHash: string | undefined): string {
    return `${menuScreenPrefix}${menuID}-${primaryKeyHash ?? ""}`;
}
